import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, Send, Loader2, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contattaci() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Compila tutti i campi per continuare.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata! Ti risponderemo entro 24h.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Qualcosa è andato storto. Riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contattaci" data-testid="section-contattaci" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-purple-300/80 mb-4">07 · Contattaci</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
              Pronto a far <br /> crescere il tuo <span className="text-gradient">brand</span>?
            </h2>
            <p className="mt-6 text-slate-300 text-base md:text-lg max-w-md">
              Raccontaci il tuo progetto. Ti risponderemo con un&apos;analisi gratuita e una prima
              proposta strategica cucita sulla tua realtà.
            </p>

            <div className="mt-10 space-y-4">
              <a href="mailto:info@socialwhale.it" data-testid="contact-info-email" className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-white/20 transition">
                <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-blue-500/30 border border-white/10 flex items-center justify-center text-purple-300">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Email</div>
                  <div className="text-white text-sm md:text-base">info@socialwhale.it</div>
                </div>
              </a>
              <a href="tel:+393513269205" data-testid="contact-info-phone" className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-white/20 transition">
                <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-blue-500/30 border border-white/10 flex items-center justify-center text-purple-300">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">Telefono</div>
                  <div className="text-white text-sm md:text-base">+39 351 326 9205</div>
                </div>
              </a>
              <a
                href="/staff"
                data-testid="staff-cta"
                className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-white/25 transition group"
                title="Accesso riservato al team"
              >
                <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500/40 to-blue-500/40 border border-white/15 flex items-center justify-center text-purple-200 group-hover:scale-105 transition-transform">
                  <Lock size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest text-slate-400">Area riservata</div>
                  <div className="text-white text-sm md:text-base flex items-center gap-2">
                    STAFF
                    <span className="text-[10px] font-medium uppercase tracking-widest rounded-full bg-gradient-to-r from-purple-500/30 to-blue-500/30 border border-white/15 px-2 py-0.5 text-purple-100">
                      Login
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 glass gradient-border rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="text-xs uppercase tracking-widest text-slate-400">Nome</label>
                <input id="name" name="name" type="text" value={form.name} onChange={onChange} data-testid="contact-input-name" required placeholder="Come ti chiami?" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 focus:bg-white/[0.05] outline-none transition" />
              </div>
              <div>
                <label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-400">Email</label>
                <input id="email" name="email" type="email" value={form.email} onChange={onChange} data-testid="contact-input-email" required placeholder="nome@brand.it" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 focus:bg-white/[0.05] outline-none transition" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="message" className="text-xs uppercase tracking-widest text-slate-400">Messaggio</label>
                <textarea id="message" name="message" value={form.message} onChange={onChange} data-testid="contact-input-message" required placeholder="Parlaci del tuo brand, degli obiettivi e delle sfide che vuoi affrontare." rows={6} className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 focus:bg-white/[0.05] outline-none transition resize-none" />
              </div>
            </div>
            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
              <p className="text-xs text-slate-500 max-w-sm">
                Inviando accetti di essere contattato via email. Nessuno spam, promesso.
              </p>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {loading ? "Invio in corso…" : "Invia richiesta"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
